import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Play } from 'lucide-react'

interface VideoEmbedProps {
  url: string | null
  thumbnailUrl?: string | null
  title?: string
  className?: string
  aspectRatio?: '16/9' | '4/3' | '1/1'
}

// Extract video ID from various URL formats
function getVideoId(url: string): { platform: 'youtube' | 'vimeo' | null; id: string | null } {
  // YouTube
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  const youtubeMatch = url.match(youtubeRegex)
  if (youtubeMatch) {
    return { platform: 'youtube', id: youtubeMatch[1] }
  }

  // Vimeo
  const vimeoRegex = /(?:vimeo\.com\/)(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|video\/|)(\d+)/
  const vimeoMatch = url.match(vimeoRegex)
  if (vimeoMatch) {
    return { platform: 'vimeo', id: vimeoMatch[1] }
  }

  return { platform: null, id: null }
}

// Get embed URL for platform
function getEmbedUrl(platform: 'youtube' | 'vimeo', id: string): string {
  switch (platform) {
    case 'youtube':
      return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`
    case 'vimeo':
      return `https://player.vimeo.com/video/${id}?autoplay=1`
    default:
      return ''
  }
}

// Get thumbnail URL for platform
function getThumbnailUrl(platform: 'youtube' | 'vimeo', id: string): string {
  switch (platform) {
    case 'youtube':
      return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`
    case 'vimeo':
      // Vimeo requires API call for thumbnail, use placeholder
      return ''
    default:
      return ''
  }
}

export function VideoEmbed({
  url,
  thumbnailUrl,
  title,
  className,
  aspectRatio = '16/9',
}: VideoEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false)

  if (!url) {
    return (
      <div
        className={cn(
          'bg-muted flex items-center justify-center rounded-lg',
          className
        )}
        style={{ aspectRatio }}
      >
        <span className="text-muted-foreground">No video available</span>
      </div>
    )
  }

  const { platform, id } = getVideoId(url)

  // If URL is already an embed URL, use it directly
  if (url.includes('embed') || url.includes('player.vimeo')) {
    return (
      <div
        className={cn('relative overflow-hidden rounded-lg', className)}
        style={{ aspectRatio }}
      >
        <iframe
          src={url}
          title={title || 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      </div>
    )
  }

  if (!platform || !id) {
    return (
      <div
        className={cn(
          'bg-muted flex items-center justify-center rounded-lg',
          className
        )}
        style={{ aspectRatio }}
      >
        <span className="text-muted-foreground">Invalid video URL</span>
      </div>
    )
  }

  const embedUrl = getEmbedUrl(platform, id)
  const thumbnail = thumbnailUrl || getThumbnailUrl(platform, id)

  if (!isPlaying && thumbnail) {
    return (
      <div
        className={cn(
          'relative cursor-pointer overflow-hidden rounded-lg group',
          className
        )}
        style={{ aspectRatio }}
        onClick={() => setIsPlaying(true)}
      >
        {/* Thumbnail */}
        <img
          src={thumbnail}
          alt={title || 'Video thumbnail'}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/40">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-110">
            <Play className="h-8 w-8 text-black ml-1" fill="currentColor" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn('relative overflow-hidden rounded-lg', className)}
      style={{ aspectRatio }}
    >
      <iframe
        src={embedUrl}
        title={title || 'Video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
    </div>
  )
}
